/**
 * The remediation ladder's fixed figures and words (Warren, 28 Oct 2026, evening addendum item 1: "When something goes
 * wrong the system fixes it itself (soft reset or other commands), and only when that fails asks the building's
 * facilities contact for a hard reset at the breaker"). Shared by the Api (lib/remediation) and Admin (the kill switch,
 * the charger card), so both read one set of limits and one set of labels.
 *
 *   a   TriggerMessage (StatusNotification, Heartbeat, BootNotification). Live at deploy.
 *   b1  ChangeAvailability Inoperative then Operative on the connector.
 *   b2  UnlockConnector, when the fault says the cable is locked with no session.
 *   b3  Soft Reset, only when no session is active on any connector.
 *   c   one Hard Reset, when the soft reset did not clear it and the charger is idle.
 *   d   ask the building: the breaker off for 30 seconds, then back on, and a Done link. Live at deploy.
 *   e   a service call to EVI ops and the installer. Live at deploy.
 *
 * b and c run in shadow for 3 days from the first sweep (they log what they would have done), then go live by
 * themselves unless shadow is kept on in Admin. The limits here are Warren's: Admin can only lower them.
 */
export const REMEDIATION_STEPS = ['a', 'b1', 'b2', 'b3', 'c', 'd', 'e'];
export const REMEDIATION_MODES = ['live', 'shadow'];
/** The switches in Admin: b covers b1, b2 and b3. */
export const REMEDIATION_SWITCHES = ['a', 'b', 'c', 'd', 'e'];
export const REMEDIATION_WAITING_ON = ['building', 'installer', 'evi'];
/** "b and c run 3 days in shadow mode (log would have done), then go live automatically unless I say stop." */
export const REMEDIATION_SHADOW_MS = 3 * 24 * 60 * 60_000;
/** Warren's limits. A saved setting (network-wide or one charger's) may be lower, never higher. */
export const REMEDIATION_LIMITS = {
    /** "At most 1 soft reset per 6 hours and 2 per 24 hours per charger." */
    soft_per_6h: 1,
    soft_per_24h: 2,
    /** "one Hard Reset, at most 1 per 24 hours." */
    hard_per_24h: 1,
    /** Kept from stage 6 (ruling 6, 29 Oct): resets reach at most 3 chargers at one location a day. */
    site_resets_per_day: 3,
};
export const REMEDIATION_LIMIT_KEYS = Object.keys(REMEDIATION_LIMITS);
export const REMEDIATION_LIMIT_LABELS = {
    soft_per_6h: 'Soft resets per charger in 6 hours',
    soft_per_24h: 'Soft resets per charger in 24 hours',
    hard_per_24h: 'Hard resets per charger in 24 hours',
    site_resets_per_day: 'Chargers reset per location in a day',
};
/** Each limit at most Warren's, a whole number, never below 0. Anything missing or unreadable is Warren's figure. */
export function lowerOnlyLimits(saved, ceiling = REMEDIATION_LIMITS) {
    const out = { ...ceiling };
    for (const key of REMEDIATION_LIMIT_KEYS) {
        const v = saved?.[key];
        if (typeof v === 'number' && Number.isInteger(v) && v >= 0)
            out[key] = Math.min(v, ceiling[key]);
    }
    return out;
}
export const REMEDIATION_STEP_LABELS = {
    a: 'Asked the charger to report',
    b1: 'Restarted the connector',
    b2: 'Unlocked the cable',
    b3: 'Soft reset',
    c: 'Hard reset',
    d: 'Asked the building',
    e: 'Service call',
};
export const REMEDIATION_SWITCH_LABELS = {
    a: 'a. Ask a quiet charger to report (TriggerMessage)',
    b: 'b. Restart the connector, unlock the cable, soft reset',
    c: 'c. One hard reset',
    d: 'd. Ask the building to cycle the breaker',
    e: 'e. Service call to EVI ops and the installer',
};
export const REMEDIATION_WAITING_LABELS = {
    building: 'Waiting on the building',
    installer: 'Waiting on the installer',
    evi: 'Waiting on EVI',
};
/** The b and c steps: shadow for the first 3 days. */
export const isShadowableStep = (step) => step === 'b1' || step === 'b2' || step === 'b3' || step === 'c';
/** The Admin switch a step answers to. */
export const switchOfStep = (step) => (step === 'b1' || step === 'b2' || step === 'b3' ? 'b' : step);
/** How long a charger's pause lasts from the Admin card; 'until_resumed' is open-ended. */
export const REMEDIATION_PAUSE_CHOICES = ['1h', '24h', '7d', 'until_resumed'];
export const REMEDIATION_PAUSE_LABELS = {
    '1h': 'Pause 1 hour', '24h': 'Pause 24 hours', '7d': 'Pause 7 days', until_resumed: 'Pause until resumed',
};
/** "Until resumed" is stored as this far date, so every pause is one comparison. */
export const REMEDIATION_PAUSE_FOREVER = new Date('2999-12-31T00:00:00.000Z');
export function pauseUntil(choice, now) {
    switch (choice) {
        case '1h': return new Date(now.getTime() + 60 * 60_000);
        case '24h': return new Date(now.getTime() + 24 * 60 * 60_000);
        case '7d': return new Date(now.getTime() + 7 * 24 * 60 * 60_000);
        default: return REMEDIATION_PAUSE_FOREVER;
    }
}
/** The exact step the building is asked for (Warren: "turn the charger's breaker off for 30 seconds, then back on"). */
export const BREAKER_STEP = 'Turn the charger\'s breaker off for 30 seconds, then back on, then press Done.';
export const MANY_BREAKER_STEP = 'Turn the breaker for each of these chargers off for 30 seconds, then back on, then press Done.';
export const SITE_BREAKER_STEP = 'Turn the breaker or panel that feeds the chargers off for 30 seconds, then back on, then press Done.';
//# sourceMappingURL=remediationLadder.js.map